import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import convertMoney from "shared/lib/convertMoney";
import useLoginCookie from "shared/model/useLoginCookie";
import { supabase } from "shared/lib/supabase";
import useGameLogic from "./useGameLogic";
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';

// Extend dayjs with plugins
dayjs.extend(duration);
dayjs.extend(utc);

const useSupabaseRealtime = (success, setInfoData,initialEventData) => {
  const channelRef = useRef();
  const reconnectTimeoutRef = useRef();
  const isReconnectingRef = useRef(false);
  const shouldReloadOnReconnectRef = useRef(false);
  const { idx } = useParams();
  const [loginCookie] = useLoginCookie();

	const [supabaseConnectError, setSupabaseConnectError] = useState(false)
  const [supabaseConnectErrorStatus, setSupabaseConnectErrorStatus] = useState('')

  const [reconnectCounter, setReconnectCounter] = useState(0)
  const [durationList, setDurationList] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [eventData, setEventData] = useState(null); // Store raw event data from database
  const [timeData, setTimeData] = useState({
    is_level_up: false,
    level: "1",
    time: "00:00",
    sb: "0",
    bb: "0",
    ante: "0",
    next_sb: "0",
    next_bb: "0",
    next_ante: "0",
    elapsed_time: "00:00:00",
    next_break: "00:00:00",
    reg_close: "00:00:00"
  });
  const [oldTimeData, setOldTimeData] = useState(null);
  // Consolidated timer states
  const [timers, setTimers] = useState({
    countDown: null,
    elapsedTime: null,
    nextBreak: null,
    regClose: null,
    elapsedTimeDisplay: null
  });
  
  // Use game logic hook to process raw event data
  const { formattedGameData } = useGameLogic(eventData, durationList, setInfoData, idx, timeData);
  // Update time data when formatted game data changes
  useEffect(() => {
    if (formattedGameData) {
      formatTimeData(setTimeData, formattedGameData, setTimers, oldTimeData, durationList);
    }
  }, [formattedGameData, oldTimeData, durationList]);
  

  // Reconnection function
  const attemptReconnect = useCallback(() => {
    if (isReconnectingRef.current) return; // Already reconnecting
    
    isReconnectingRef.current = true;
    shouldReloadOnReconnectRef.current = true;
    console.log('Attempting to reconnect to Supabase Realtime...');
    
    // Clean up existing channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    
    // Wait 2 seconds before reconnecting
    reconnectTimeoutRef.current = setTimeout(() => {
      isReconnectingRef.current = false;
      // Trigger reconnection by incrementing reconnectCounter
      setReconnectCounter(prev => prev + 1);
    }, 2000);
  }, []);

  // Supabase real-time connection
  useEffect(() => {
    if (!success || !idx) return;
    if (channelRef.current && !isReconnectingRef.current) return; 
  
    setIsLoading(true);
    const channel = supabase
      .channel('event-realtime')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema:  process.env.REACT_APP_SUPABASE_SCHEMA,
          table: 'event',
        },
        (payload) => {
          if (payload.new.idx !== Number(idx)) return;
  
          console.log('Event update received!');
          let newData = payload.new.event_timer;
          let oldData = payload.old.event_timer;
  
          if (newData) {
            newData.server_time = payload.new.current_time
            setEventData(newData);
            if (payload.new.duration_list) {
              setDurationList(payload.new.duration_list);
            }
          }
  
          if (oldData) {
            oldData.server_time = payload.old.current_time
            setOldTimeData(oldData);
          }
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
        switch (status) {
          case 'SUBSCRIBED':
            console.log('Supabase real-time connection successful');
            setIsLoading(false);
            setSupabaseConnectError(false);
            isReconnectingRef.current = false;
            
            // Reload page if we reconnected after an error
            if (shouldReloadOnReconnectRef.current) {
              console.log('Reconnection successful, reloading page...');
              shouldReloadOnReconnectRef.current = false;
              window.location.reload();
            }
            break;
          case 'CHANNEL_ERROR':
            handleSupabaseConnectError(status, 'CHANNEL_ERROR', setSupabaseConnectErrorStatus, setSupabaseConnectError, attemptReconnect);
            break;
          case 'TIMED_OUT':
            handleSupabaseConnectError(status, 'TIMED_OUT', setSupabaseConnectErrorStatus, setSupabaseConnectError, attemptReconnect);
            break;
          case 'CLOSED':
            handleSupabaseConnectError(status, 'CLOSED', setSupabaseConnectErrorStatus, setSupabaseConnectError, attemptReconnect);
            break;
          default:
            break;
        }
      });
  
    channelRef.current = channel;
  
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        console.log('Supabase disconnection');
        channelRef.current = null;
      }
    };
  }, [success, setInfoData, idx, loginCookie, reconnectCounter, attemptReconnect]);
  


  // initial data
  useEffect(() => {
    if(initialEventData?.data){
      setEventData({
        ...initialEventData?.data?.event_timer,
        server_time: initialEventData?.data?.current_time,
        ...(initialEventData?.data?.event_timer?.pause_date && {start_time: initialEventData?.data?.event_timer?.start_time + 
          initialEventData?.data?.current_time - initialEventData?.data?.event_timer?.pause_date})
      });
      if (initialEventData?.data?.duration_list) {
        setDurationList(initialEventData?.data?.duration_list);
      }
    }
  }, [initialEventData])

  return {
    timeData, 
    oldTimeData,
    durationList, 
    isLoading, 
    countDown: timers.countDown,
    elapsedTime: timers.elapsedTime,
    nextBreak: timers.nextBreak,
    regClose: timers.regClose,
    elapsedTimeDisplay: timers.elapsedTimeDisplay,
    supabaseConnectError,
    supabaseConnectErrorStatus
  };
};

export default useSupabaseRealtime;

const formatTimeData = (setTimeData, data, setTimers, old_data, durationList) => {
  const newTimers = calculateAllTimers(data, old_data, durationList);
  setTimers(prevTimers => ({ ...prevTimers, ...newTimers }));
  setTimeData({
    is_level_up: data?.is_level_up,
    level: data?.level ? data?.level : "1",
    time: data?.time ? data?.time : "00:00",
    sb: data?.sb ? convertMoney(data?.sb) : "0",
    bb: data?.bb ? convertMoney(data?.bb) : "0",
    ante: data?.ante ? convertMoney(data?.ante) : "0",
    next_level: data?.next_level ? data?.next_level : "0",
    next_sb: data?.next_sb ? convertMoney(data?.next_sb) : "0",
    next_bb: data?.next_bb ? convertMoney(data?.next_bb) : "0",
    next_ante: data?.next_ante ? convertMoney(data?.next_ante) : "0",
    level_after_break: data?.level_after_break ? data?.level_after_break : null,
    level_after_break_sb: data?.level_after_break_sb ? convertMoney(data?.level_after_break_sb) : "0",
    level_after_break_bb: data?.level_after_break_bb ? convertMoney(data?.level_after_break_bb) : "0",
    level_after_break_ante: data?.level_after_break_ante ? convertMoney(data?.level_after_break_ante) : "0",
    elapsed_time: data?.total_time ? data?.total_time : "00:00:00",
    next_break: data?.next_break_time ? data?.next_break_time : "00:00:00",
    reg_close: data?.reg_end_time ? data?.reg_end_time : "00:00:00",
    rest_player: data?.rest_player ?? "0",
    total_player: data?.total_player ?? "0",
    re_entry: data?.re_entry ?? "0",
    early_count: data?.early_count ?? "0",
    avg_stack: data?.avg_stack ?? "0",
    total_stack: data?.total_stack ?? "0",
    pause: data?.pause || false,
    event_state: data?.event_state || "unknown", // Add event_state to timeData
  });
};

// Calculate elapsed time display based on completed rounds and current progress
const calculateElapsedTimeDisplay = (durationList, currentLevel, currentTime) => {
  if (!durationList || !currentLevel) return "00:00:00";
  
  try {
    // Parse durationList if it's a string
    const parsedDurationList = typeof durationList === 'string' ? JSON.parse(durationList) : durationList;
    
    let totalElapsedMinutes = 0;
    let currentLevelNumber = parseInt(currentLevel);
    let foundCurrentLevel = false;
    
    // Calculate total time from completed rounds
    for (let i = 0; i < parsedDurationList.length; i++) {
      const item = parsedDurationList[i];
      const levelOrBreak = item[0];
      const durationMinutes = parseInt(item[1]);
      
      if (levelOrBreak === "break") {
        // Add break time to total
        totalElapsedMinutes += durationMinutes;
      } else {
        const levelNumber = parseInt(levelOrBreak);
        
        if (levelNumber < currentLevelNumber) {
          // This is a completed level, add full duration
          totalElapsedMinutes += durationMinutes;
        } else if (levelNumber === currentLevelNumber) {
          // This is the current level, add partial time based on current progress
          foundCurrentLevel = true;
          
          // Parse current time to get elapsed time in current level
          if (currentTime && currentTime !== "00:00") {
            const [minutes, seconds] = currentTime.split(':').map(Number);
            const totalLevelSeconds = durationMinutes * 60;
            const remainingSeconds = minutes * 60 + seconds;
            const elapsedInCurrentLevel = (totalLevelSeconds - remainingSeconds) / 60;
            
            totalElapsedMinutes += Math.max(0, elapsedInCurrentLevel);
          }
          break;
        }
      }
    }
    
    // If we couldn't find the current level, just use all completed levels
    if (!foundCurrentLevel && currentLevelNumber > 1) {
      // Find the last level in duration list and assume we're past it
      let lastLevelFound = false;
      for (let i = parsedDurationList.length - 1; i >= 0; i--) {
        const item = parsedDurationList[i];
        if (item[0] !== "break") {
          lastLevelFound = true;
          break;
        }
      }
      
      if (lastLevelFound) {
        // We're past the last level, don't add any more time
        // totalElapsedMinutes stays as calculated from completed levels
      }
    }
    
    // Convert minutes to HH:MM:SS format
    const totalSeconds = Math.floor(totalElapsedMinutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    
  } catch (error) {
    console.error('Error calculating elapsed time display:', error);
    return "00:00:00";
  }
};

// Simplified timer calculation - now just passes through the values from game logic
const calculateAllTimers = (data, old_data, durationList) => {
  if (!data) return {};

  const timers = {};

  // Use the calculated values directly from game logic (no need for time difference calculations)
  if (data?.time) {
    timers.countDown = data.time;
    // timers.countDown = data.reg_end_time;
  }

  if (data?.total_time) {
    timers.elapsedTime = data.total_time;
  }

  if (data?.next_break_time) {
    timers.nextBreak = data.next_break_time;
  }

  if (data?.reg_end_time) {
    timers.regClose = data.reg_end_time;
  }

  // Calculate elapsedTimeDisplay based on durationList and current level
  if (durationList && data?.level && data?.time) {
    timers.elapsedTimeDisplay = calculateElapsedTimeDisplay(durationList, data.level, data.time);
  }

  return timers;
};

const handleSupabaseConnectError = (status,statusCode,setSupabaseConnectErrorStatus,setSupabaseConnectError,attemptReconnect) => {
  console.warn('Supabase real-time connection failed:', status);
  setSupabaseConnectErrorStatus(statusCode);
  setSupabaseConnectError(true);
  attemptReconnect();

}
